/** The column names the server recognises as a creator sheet's header, so the
 *  upload preview can pick the same header row it will. Hand-kept copy of
 *  discovr's internal/campaigns/admin_import_preview_handler.go (genericUsernameAliases,
 *  looseUsernameAliases, usernameAliasesByPlatform, looseDeliverableAliases,
 *  fullNameAliases, fieldKeywords, fieldAliases) and admin_handler.go
 *  (normalizeHeader, platformShortAlias). When one changes, change both —
 *  sheetHeaderAliases.test.ts pins the important entries so drift shows up. */
import type { ExpectedField } from '@discovr/types';

const GENERIC_USERNAME_ALIASES = ['username', 'un', 'handle'];
const LOOSE_USERNAME_ALIASES = ['profile', 'profileurl', 'profilelink', 'link', 'url'];
const USERNAME_ALIASES_BY_PLATFORM: Readonly<Record<string, readonly string[]>> = {
  instagram: [
    'instagram', 'instagramprofile', 'instagramlink', 'iglink', 'ig',
    'instagramhandle', 'instagramusername', 'ighandle', 'igusername', 'instahandle', 'instausername',
    'instagramprofilelink', 'instaid', 'instagramid', 'igid', 'insta',
  ],
  youtube: [
    'youtube', 'youtubechannel', 'channel', 'channelurl', 'ytlink', 'yt',
    'youtubehandle', 'youtubeusername', 'channelhandle', 'ythandle', 'channellink',
  ],
};
const LOOSE_DELIVERABLE_ALIASES: Readonly<Record<string, readonly string[]>> = {
  instagram_non_collab_reel: ['reel', 'reels', 'reelprice', 'reelrate', 'reelcost', 'costperreel', 'cost', 'commercials', 'rate', 'price', 'fee'],
  instagram_stories: ['storyprice', 'story'],
};
const FULL_NAME_ALIASES = ['name', 'creatorname', 'influencername', 'fullname'];

const ALIASES_BY_FIELD_KEY: Readonly<Record<string, readonly string[]>> = {
  first_name: ['firstname', 'first_name', 'first name', 'fn'],
  last_name: ['lastname', 'last_name', 'last name', 'ln'],
  engagement_rate: ['engagement_rate', 'engagementrate', 'eng_rate', 'er'],
  avg_views: ['avg_views', 'avgviews', 'average_views', 'av', 'avgreelviews', 'views'],
  followers: ['followers', 'subscribers', 'subs', 'followercount', 'followerscount', 'nooffollowers'],
  usage_rights_days: ['usagerightsdays', 'usagedays', 'urdays'],
  email: ['email', 'emailid', 'emailaddress', 'mail', 'contactemail'],
};

/** The fixed creator/audience columns, used while the campaign's own field
 *  list has not loaded yet. */
const FIXED_FIELD_KEYS = ['username', 'first_name', 'last_name', 'engagement_rate', 'avg_views', 'followers', 'usage_rights_days', 'email'];

const FIRST_NAME_KEY = 'first_name';
const USERNAME_KEY = 'username';
const DELIVERABLE_PREFIX_ALIASES: ReadonlyArray<readonly [string, string]> = [
  ['instagram_', 'ig_'],
  ['youtube_', 'yt_'],
];

/** A header's letters and digits, lower-cased: "Usage Rights (days)" and
 *  "usage_rights_days" compare equal. Mirrors the backend's normalizeHeader. */
export function normalizeHeader(text: string): string {
  return Array.from(text)
    .filter((char) => /[\p{L}\p{Nd}]/u.test(char))
    .map((char) => char.toLowerCase())
    .join('');
}

function platformShortAlias(key: string): string[] {
  const prefixes = DELIVERABLE_PREFIX_ALIASES.find(([long]) => key.startsWith(long));
  return prefixes ? [`${prefixes[1]}${key.slice(prefixes[0].length)}`] : [];
}

function fieldAliases(key: string, platform: string | undefined): readonly string[] {
  if (key === USERNAME_KEY) return [...GENERIC_USERNAME_ALIASES, ...(USERNAME_ALIASES_BY_PLATFORM[platform ?? ''] ?? [])];
  return ALIASES_BY_FIELD_KEY[key] ?? [key, ...platformShortAlias(key)];
}

function fieldKeywords(field: Pick<ExpectedField, 'key' | 'label'>, platform: string | undefined): string[] {
  const keywords = [...fieldAliases(field.key, platform)];
  // An empty label would match a blank header cell.
  if (field.label !== '') keywords.push(field.label);
  if (field.key === USERNAME_KEY) keywords.push(...LOOSE_USERNAME_ALIASES);
  keywords.push(...(LOOSE_DELIVERABLE_ALIASES[field.key] ?? []));
  // The backend adds these only for a sheet without a first/last split, but a
  // split header is itself a known column, so for "any known header" they agree.
  if (field.key === FIRST_NAME_KEY) keywords.push(...FULL_NAME_ALIASES);
  return keywords;
}

/** Every normalised header spelling that names one of the campaign's fields —
 *  what the backend's suggestFieldMapping can match. Falls back to the fixed
 *  creator columns while the campaign's field list is empty. */
export function knownHeaderNames(expectedFields: ExpectedField[], campaignPlatform: string | undefined): Set<string> {
  const platform = campaignPlatform?.trim().toLowerCase();
  const fields = expectedFields.length > 0 ? expectedFields : FIXED_FIELD_KEYS.map((key) => ({ key, label: '' }));
  const names = fields.flatMap((field) => fieldKeywords(field, platform).map(normalizeHeader));
  return new Set(names.filter((name) => name !== ''));
}

/** The " (2)", " (3)"... the backend appends to a repeated column header
 *  ("Followers", "Followers (2)"). N is never below 2, so a sheet's own
 *  "Followers (1)" keeps its meaning. A header is matched as written first,
 *  then without it. Mirrors the backend's duplicateHeaderSuffixRE. */
const DUPLICATE_HEADER_SUFFIX = /\s*\((?:[2-9]|[1-9][0-9]+)\)\s*$/;

/** Whether a row holds at least one known column name: the backend's test for
 *  "this is the header, not a title above it". */
export function rowHasKnownHeader(row: readonly string[], knownNames: ReadonlySet<string>): boolean {
  return row.some(
    (cell) => knownNames.has(normalizeHeader(cell)) || knownNames.has(normalizeHeader(cell.replace(DUPLICATE_HEADER_SUFFIX, ''))),
  );
}
